import { DailyLightInfoResponse, DayStatus, EngineMode } from "../types";
import { Moment } from "moment";
import { selectionStore } from "../stores/selection.store";

class CalendarService {
  private static service: CalendarService | undefined;
  private _dailyAvailabilities: { [key: string]: DayStatus } | undefined;
  private _dailyEngine: { [key: string]: EngineMode } | undefined;
  readonly DATE_NOW: Date = (() => {
    let date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  })();

  readonly FORMAT: string = "YYYY-MM-DD";

  static get(): CalendarService {
    if (!this.service) {
      this.service = new CalendarService();
    }
    return this.service!;
  }

  set dailyAvailabilities(
    dailyAvailabilitiesData: { [key: string]: DayStatus } | undefined
  ) {
    this._dailyAvailabilities = dailyAvailabilitiesData;
  }

  get dailyAvailabilities() {
    return this._dailyAvailabilities;
  }

  set dailyEngine(dailyEngineData: { [key: string]: EngineMode } | undefined) {
    this._dailyEngine = dailyEngineData;
  }

  get dailyEngine() {
    return this._dailyEngine;
  }

  setupServiceData(dailyLightInfoResponseData: DailyLightInfoResponse) {
    this.computeDailyAvailabilities(dailyLightInfoResponseData);
    this.computeDailyEngine(dailyLightInfoResponseData);
    this.controlSelectedDate();
  }

  getEngineMode(date: Moment): EngineMode {
    return this.dailyEngine
      ? this.dailyEngine[this.formattedDate(date)]
      : EngineMode.capacity;
  }

  isDayInThePast(day: Moment): boolean {
    return day.diff(this.DATE_NOW) < 0;
  }

  isDayBlocked(day: Moment): boolean {
    if (this.isDayInThePast(day)) return true;
    const status = this.getStatus(day);
    if (status === null) return true;
    return [DayStatus.full, DayStatus.closed].includes(status);
  }

  getStatus(date: Moment): DayStatus | null {
    return this.dailyAvailabilities
      ? this.dailyAvailabilities![this.formattedDate(date)]
      : null;
  }

  private formattedDate(date: Moment): string {
    return date.format(this.FORMAT);
  }

  private computeDailyAvailabilities(
    dailyLightInfoResponseData: DailyLightInfoResponse
  ) {
    this.dailyAvailabilities = Object.assign(
      {},
      ...dailyLightInfoResponseData.daily_availabilities.map((daily_avail) => ({
        [daily_avail.date]: daily_avail.status,
      }))
    );
  }

  private computeDailyEngine(
    dailyLightInfoResponseData: DailyLightInfoResponse
  ) {
    this.dailyEngine = Object.assign(
      {},
      ...dailyLightInfoResponseData.daily_availabilities.map((daily_avail) => ({
        [daily_avail.date]: daily_avail.engine_mode,
      }))
    );
  }

  private controlSelectedDate() {
    if (!selectionStore.date) return;
    let selectedDate = selectionStore.date;
    let status = this.getStatus(selectedDate);
    if (status === null || status === "unavailable")
      return selectionStore.resetDate();
  }
}

export default CalendarService.get();
